@if (project(); as projectData) {
    <p-toast></p-toast>
    <app-page-shell>
        <div class="posm-page">

            <app-page-detail-hero
                eyebrow="POSM Execution"
                icon="pi pi-briefcase"
                [title]="projectData.projectName || 'POSM Execution'"
                [subtitle]="detailSubtitle(projectData)">
                <div tags class="posm-hero-tags">
                    <div class="posm-hero-status" [ngClass]="heroStatusClass(projectData.status)">
                        <span><i class="pi pi-circle-fill"></i> Execution Status</span>
                        <strong>{{ projectData.status || 'Draft' }}</strong>
                    </div>
                    <div class="posm-hero-meta-tags">
                        <p-tag [value]="projectData.projectNumber || '-'" severity="info"></p-tag>
                        @if (projectJobTypeGroup(projectData) !== projectJobTypeLabel(projectData)) {
                            <p-tag [value]="projectJobTypeGroup(projectData)" severity="secondary"></p-tag>
                        }
                        <p-tag [value]="projectJobTypeLabel(projectData)" severity="info"></p-tag>
                        <p-tag [value]="projectData.jobPriority || 'Normal'" severity="secondary"></p-tag>
                    </div>
                </div>
                <div actions>
                    <p-button label="Executions" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/posm/executions"></p-button>
                </div>
            </app-page-detail-hero>

            <div class="posm-detail-layout">
                <main class="posm-detail-main">
                    <app-page-metric-strip [metrics]="detailMetrics()"></app-page-metric-strip>

                    <app-page-data-panel
                        title="Execution Workspace"
                        subtitle="Stores, materials, delivery schedule, transport releases, execution tasks, and execution tracking in one operating view."
                        [resultText]="projectData.projectNumber || 'DRAFT'">
                        <nav class="posm-workspace-tabs" aria-label="POSM sections">
                            @for (tab of tabs; track tab.id) {
                                <button type="button" class="workspace-tab-button" [class.active]="activeTab() === tab.id" (click)="setTab(tab.id)">
                                    <span class="workspace-tab">
                                        <i [class]="tab.icon"></i>
                                        <span>{{ tab.label }}</span>
                                        @if (tabBadge(tab.id); as badge) {
                                            <span class="workspace-badge" [class.info]="tabBadgeTone(tab.id) === 'info'" [class.success]="tabBadgeTone(tab.id) === 'success'" [class.neutral]="tabBadgeTone(tab.id) === 'neutral'">{{ badge }}</span>
                                        }
                                    </span>
                                </button>
                            }
                        </nav>

                        <div class="workspace-section">
                            @if (activeTab() === 'overview') {
                                <app-posm-project-overview
                                    [project]="projectData"
                                    [stores]="projectStores()"
                                    [storesLoading]="projectStoresLoading()"
                                    [workspaceSummary]="workspaceSummary()"
                                    (onDataChanged)="refreshProjectWorkspace()"></app-posm-project-overview>
                            }
                            @if (activeTab() === 'materials') {
                                <app-posm-project-materials
                                    [project]="projectData"
                                    (onDataChanged)="refreshProjectWorkspace()"></app-posm-project-materials>
                            }
                            @if (activeTab() === 'delivery-schedule') {
                                <app-posm-project-delivery-schedule
                                    [project]="projectData"
                                    (onDataChanged)="refreshProjectWorkspace()"></app-posm-project-delivery-schedule>
                            }
                            @if (activeTab() === 'productivity') {
                                <app-posm-project-productivity [project]="projectData"></app-posm-project-productivity>
                            }
                            @if (activeTab() === 'truck-alignment') {
                                <app-posm-project-truck-alignment [project]="projectData"></app-posm-project-truck-alignment>
                            }
                            @if (activeTab() === 'execution') {
                                <app-posm-project-execution [project]="projectData" [workspaceSummary]="workspaceSummary()"></app-posm-project-execution>
                            }
                            @if (activeTab() === 'completion-reports') {
                                <app-posm-execution-completion-reports [project]="projectData"></app-posm-execution-completion-reports>
                            }
                        </div>
                    </app-page-data-panel>
                </main>

                <aside class="business-action-pane" aria-label="POSM Command Center">
                    <header class="business-action-pane__header">
                        <span class="business-action-pane__eyebrow">POSM Execution</span>
                        <h2>Command Center</h2>
                        <p>Project actions and commands for the current tab.</p>
                    </header>

                    <section class="business-action-group">
                        <div class="business-action-group__heading">
                            <i class="pi pi-briefcase"></i>
                            <div><strong>Execution</strong><span>Manage this POSM project.</span></div>
                        </div>
                        @if (canEditProject()) {
                            <p-button label="Edit Header" icon="pi pi-pencil" severity="secondary" [outlined]="true"
                                [routerLink]="['/posm/executions', projectData.id, 'edit']"></p-button>
                        }
                        <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true"
                            [loading]="isRefreshBusy()" (click)="refreshView()"></p-button>
                        <p-button label="Export Delivery Schedule" icon="pi pi-download" severity="secondary" [outlined]="true"
                            [loading]="exportLoading()" (click)="exportDeliverySchedule()"></p-button>
                        @if (projectData.logisticsProjectId) {
                            <p-button label="Open Logistics Project" icon="pi pi-sitemap" severity="secondary" [outlined]="true" (click)="openLogisticsProject()"></p-button>
                        }
                    </section>

                    @if (activeTab() === 'materials') {
                        @if (materialsTab(); as materials) {
                            @if (materials.showWarehousingReservationsShortcut()) {
                                <section class="business-action-group">
                                    <div class="business-action-group__heading">
                                        <i class="pi pi-box"></i>
                                        <div><strong>Materials</strong><span>Open the stock reservation workbench.</span></div>
                                    </div>
                                    <p-button label="Warehousing Reservations" icon="pi pi-box" severity="secondary" [outlined]="true" routerLink="/warehousing/reservations"></p-button>
                                </section>
                            }
                        }
                    }

                    @if (activeTab() === 'delivery-schedule') {
                        @if (scheduleTab(); as schedule) {
                            <section class="business-action-group business-action-group--primary">
                                <div class="business-action-group__heading">
                                    <i class="pi pi-calendar"></i>
                                    <div><strong>Delivery Schedule</strong><span>Act on the selected schedules.</span></div>
                                </div>
                                @if (schedule.canAddSchedule()) {
                                    <p-button label="Add Schedule" icon="pi pi-plus" (click)="schedule.openNewSchedule()"></p-button>
                                }
                                @if (canEditProject()) {
                                    <p-button label="Import Excel" icon="pi pi-file-excel" severity="secondary" [outlined]="true"
                                        [routerLink]="['/posm/executions', projectData.id, 'import-schedules']"></p-button>
                                }
                                <p-button [label]="'Auto Allocate' + (schedule.selectedCount() ? ' (' + schedule.selectedCount() + ')' : '')"
                                    icon="pi pi-link" severity="secondary" [outlined]="true" [loading]="schedule.bulkAllocating()"
                                    [disabled]="!schedule.selectedCount()" (click)="schedule.applyBulkAllocation()"></p-button>
                                <p-button [label]="'Generate Installation Releases' + (schedule.selectedInstallationEligibleCount() ? ' (' + schedule.selectedInstallationEligibleCount() + ')' : '')"
                                    icon="pi pi-send" [loading]="schedule.releaseGenerating()" [disabled]="!schedule.selectedInstallationEligibleCount()"
                                    (click)="schedule.generateOrderReleases()"></p-button>
                                @if (schedule.supportsRemovalFlow()) {
                                    <p-button [label]="'Generate Removal Releases' + (schedule.selectedRemovalEligibleCount() ? ' (' + schedule.selectedRemovalEligibleCount() + ')' : '')"
                                        icon="pi pi-replay" severity="secondary" [outlined]="true" [loading]="schedule.releaseGenerating()"
                                        [disabled]="!schedule.selectedRemovalEligibleCount()" (click)="schedule.generateRemovalReleases()"></p-button>
                                }
                            </section>

                            <section class="business-action-group">
                                <div class="business-action-group__heading">
                                    <i class="pi pi-calendar-plus"></i>
                                    <div><strong>Bulk Dates</strong><span>Apply dates to the current selection.</span></div>
                                </div>
                                <label class="posm-command-date" for="posmMassInstallationDate"><span>Mass Installation Date</span>
                                    <p-datePicker inputId="posmMassInstallationDate" [ngModel]="schedule.bulkInstallationDate()" (ngModelChange)="schedule.bulkInstallationDate.set($event)"
                                        [showIcon]="true" appendTo="body" styleClass="w-full"></p-datePicker>
                                </label>
                                <p-button label="Apply Installation Date" icon="pi pi-check" [loading]="schedule.bulkSaving()"
                                    [disabled]="!schedule.selectedCount() || !schedule.bulkInstallationDate()" (click)="schedule.applyBulkInstallationDate()"></p-button>
                                @if (schedule.supportsRemovalFlow()) {
                                    <label class="posm-command-date" for="posmMassRemovalDate"><span>Mass Removal Date</span>
                                        <p-datePicker inputId="posmMassRemovalDate" [ngModel]="schedule.bulkRemovalDate()" (ngModelChange)="schedule.bulkRemovalDate.set($event)"
                                            [showIcon]="true" appendTo="body" styleClass="w-full"></p-datePicker>
                                    </label>
                                    <p-button label="Apply Removal Date" icon="pi pi-check" severity="secondary" [outlined]="true"
                                        [loading]="schedule.bulkSaving()" [disabled]="!schedule.selectedCount() || !schedule.bulkRemovalDate()"
                                        (click)="schedule.applyBulkRemovalDate()"></p-button>
                                }
                            </section>

                            <section class="business-action-group">
                                <div class="business-action-group__heading">
                                    <i class="pi pi-filter"></i>
                                    <div><strong>Schedule List</strong><span>Control search and selection.</span></div>
                                </div>
                                <p-button [label]="schedule.filtersExpanded() ? 'Hide Search & Filters' : 'Show Search & Filters'"
                                    [icon]="schedule.filtersExpanded() ? 'pi pi-chevron-up' : 'pi pi-search'"
                                    severity="secondary" [outlined]="true" (click)="schedule.toggleFilters()"></p-button>
                                <p-button label="Clear Filters" icon="pi pi-filter-slash" severity="secondary" [outlined]="true"
                                    [disabled]="!schedule.activeFilterSummary().length" (click)="schedule.clearFilters()"></p-button>
                                <p-button label="Clear Selection" icon="pi pi-times" severity="secondary" [outlined]="true"
                                    [disabled]="!schedule.selectedCount()" (click)="schedule.clearSelection()"></p-button>
                            </section>
                        }
                    }

                    @if (activeTab() === 'truck-alignment') {
                        <section class="business-action-group">
                            <div class="business-action-group__heading">
                                <i class="pi pi-truck"></i>
                                <div><strong>Truck Alignment</strong><span>Review source schedules.</span></div>
                            </div>
                            <p-button label="Open Delivery Schedule" icon="pi pi-calendar" severity="secondary" [outlined]="true"
                                (click)="setTab('delivery-schedule')"></p-button>
                        </section>
                    }

                    @if (activeTab() === 'execution') {
                        @if (executionTab(); as execution) {
                            <section class="business-action-group business-action-group--primary">
                                <div class="business-action-group__heading">
                                    <i class="pi pi-truck"></i>
                                    <div><strong>Execution &amp; Tracking</strong><span>Commercial and live operations.</span></div>
                                </div>
                                <app-execution-sales-order-button sourceType="POSM_EXECUTION" [sourceId]="projectData.id"
                                    [owningCompanyId]="projectData.owningCompanyId" [sourceLabel]="projectData.projectNumber"></app-execution-sales-order-button>
                                <p-button [label]="execution.gpsPollingEnabled ? 'Pause Tracking' : 'Resume Tracking'"
                                    [icon]="execution.gpsPollingEnabled ? 'pi pi-pause' : 'pi pi-play'" severity="secondary" [outlined]="true"
                                    (click)="execution.toggleGpsPolling()"></p-button>
                                <p-button label="Request Store Change" icon="pi pi-plus" severity="secondary" [outlined]="true"
                                    (click)="execution.openStoreChangeDialog()"></p-button>
                            </section>
                            <section class="business-action-group">
                                <div class="business-action-group__heading">
                                    <i class="pi pi-chart-bar"></i>
                                    <div><strong>Execution Reports</strong><span>Generate an operational snapshot.</span></div>
                                </div>
                                @for (reportType of execution.reportTypeOptions; track reportType.value) {
                                    <p-button [label]="'Generate ' + reportType.label" icon="pi pi-chart-bar" severity="secondary" [outlined]="true"
                                        [loading]="execution.reportActions.isPending('generate:' + reportType.value)"
                                        (click)="execution.generateReport(reportType.value)"></p-button>
                                }
                            </section>
                        }
                    }

                    @if (activeTab() === 'completion-reports') {
                        @if (reportsTab(); as reports) {
                            <section class="business-action-group business-action-group--primary">
                                <div class="business-action-group__heading">
                                    <i class="pi pi-file-pdf"></i>
                                    <div><strong>Completion Reports</strong><span>Reconcile tasks and create a PDF.</span></div>
                                </div>
                                @if (reports.canReconcileTasks()) {
                                    <p-button label="Sync delivered tasks" icon="pi pi-check-circle" severity="secondary" [outlined]="true"
                                        [loading]="reports.reconcilingTasks()" (click)="reports.reconcileTasks()"></p-button>
                                }
                                <p-button label="Generate PDF" icon="pi pi-file-pdf" [disabled]="!reports.canGenerate()"
                                    [loading]="reports.generating()" (click)="reports.generate()"></p-button>
                            </section>
                        }
                    }

                    @if (activeTab() === 'productivity') {
                        @if (productivityTab(); as productivity) {
                            <section class="business-action-group">
                                <div class="business-action-group__heading">
                                    <i class="pi pi-list-check"></i>
                                    <div><strong>Tasks &amp; Incidents</strong><span>Submit the forms in this tab.</span></div>
                                </div>
                                <p-button label="Add Store Task" icon="pi pi-plus" (click)="productivity.saveTask()"></p-button>
                                <p-button label="Add Incident" icon="pi pi-plus" severity="warn" (click)="productivity.saveIncident()"></p-button>
                            </section>
                        }
                    }

                    @if (canDeleteProject()) {
                        <section class="business-action-group business-action-group--danger">
                            <div class="business-action-group__heading">
                                <i class="pi pi-trash"></i>
                                <div><strong>Danger Zone</strong><span>Delete an unused execution.</span></div>
                            </div>
                            <span class="posm-delete-tooltip">
                                <p-button label="Delete" icon="pi pi-trash" severity="danger" [outlined]="true"
                                    [disabled]="!deleteEligibility()?.allowed" [loading]="deleteLoading()" (click)="deleteProject()"></p-button>
                                @if (!deleteEligibility()?.allowed) {
                                    <span class="posm-delete-tooltip__trigger" tabindex="0" role="button" aria-disabled="true"
                                        [attr.aria-label]="'Delete unavailable: ' + deleteReason()" [pTooltip]="deleteReason()"
                                        tooltipPosition="left" tooltipEvent="both"></span>
                                }
                            </span>
                        </section>
                    }
                </aside>
            </div>
        </div>
    </app-page-shell>
}
